/**
 * Journey Status Panel (v2.5 SS-02 / PR B2).
 *
 * The ONE client renderer of the canonical JourneyStatusSummary contract —
 * used by the journey workspace (full) and the portfolio cards (compact), so
 * the two surfaces cannot disagree: they render the same numbers from the
 * same derivation, or the same honest "unknown".
 *
 * Rendering rules (approved G0 spec):
 *  - Workflow-unknown is EXCLUSIVE: no numerator, no percentage, anywhere —
 *    the panel says completeness is unknown and offers the admin
 *    declare-workflow affordance instead.
 *  - Group headlines are SYMMETRIC: "Technical X of Y complete · Context &
 *    governance X of Y complete · Journey X of Y complete".
 *  - "Completed with exceptions" is a rendering variant of `completed`
 *    (override flag) — never unqualified green.
 *  - Blockers render every applicable cause; counts are pluralized properly
 *    ("1 asset changed", "2 assets changed") — the server's "asset(s)"
 *    parenthetical is a wire detail, never shown to users.
 */

import React from 'react';
import type {
  JourneyStatusSummary,
  ComponentProgress,
  AttentionReason,
} from '../../types/journeyStatus';

// ============================================================================
// COPY HELPERS
// ============================================================================

/** "1 asset" / "2 assets" — no "(s)" ever reaches the UI. */
export function pluralize(count: number, singular: string, plural?: string): string {
  return `${count} ${count === 1 ? singular : (plural ?? `${singular}s`)}`;
}

/**
 * The server accumulates blocker lines with wire-format counts like
 * "2 asset(s) pending ingest" / "1 result(s) predate the current evaluator".
 * Rewrite the parenthetical into real pluralization; grammar-fix the verb in
 * the evaluator line for the singular case. Lines without the pattern pass
 * through untouched (e.g. the failure sentence with its prior-result date).
 */
export function humanizeBlocker(line: string): string {
  return line
    .replace(/(\d+) asset\(s\)/g, (_m, n: string) => pluralize(Number(n), 'asset'))
    .replace(/(\d+) result\(s\) predate/g, (_m, n: string) =>
      Number(n) === 1 ? '1 result predates' : `${n} results predate`
    )
    .replace(/(\d+) result\(s\)/g, (_m, n: string) => pluralize(Number(n), 'result'));
}

export function lifecycleLabel(summary: JourneyStatusSummary): string {
  if (summary.lifecycle === 'completed') {
    return summary.completedOverride ? 'Completed with exceptions' : 'Completed';
  }
  return {
    draft: 'Draft',
    in_progress: 'In progress',
    abandoned: 'Abandoned',
  }[summary.lifecycle];
}

export function lifecycleClass(summary: JourneyStatusSummary): string {
  if (summary.lifecycle === 'completed' && summary.completedOverride) {
    return 'jsp-lifecycle-exceptions';
  }
  return `jsp-lifecycle-${summary.lifecycle}`;
}

const ATTENTION_LABELS: Record<AttentionReason, string> = {
  stale: 'Stale evidence',
  incomplete: 'Incomplete',
  failed: 'Failed analysis',
  unknown_workflow: 'Workflow not declared',
  legacy_results: 'Pre-2.5 results',
  degraded_history: 'History incomplete',
};

const STATE_LABELS: Record<ComponentProgress['state'], string> = {
  complete: 'Complete',
  partial: 'Partial',
  failed: 'Failed',
  running: 'Running',
  pending: 'Pending',
  skipped: 'Skipped',
};

/** Symmetric group headline — the ONLY completeness sentence (G0). */
export function groupHeadline(summary: JourneyStatusSummary): string | null {
  if (!summary.workflowKnown || !summary.technical || !summary.context || !summary.journey) {
    return null;
  }
  return (
    `Technical ${summary.technical.complete} of ${summary.technical.total} complete · ` +
    `Context & governance ${summary.context.complete} of ${summary.context.total} complete · ` +
    `Journey ${summary.journey.complete} of ${summary.journey.total} complete`
  );
}

// ============================================================================
// COMPONENT
// ============================================================================

export interface JourneyStatusPanelProps {
  /** null/undefined = the server could not derive a summary (honest
   *  degradation): the panel says so, it never invents numbers. */
  summary: JourneyStatusSummary | null | undefined;
  /** Compact mode (portfolio card): headline + chip + attention only. */
  compact?: boolean;
  /** Admin affordance for legacy journeys without a workflow snapshot. */
  onDeclareWorkflow?: () => void;
  declareWorkflowBusy?: boolean;
}

export const JourneyStatusPanel: React.FC<JourneyStatusPanelProps> = ({
  summary,
  compact = false,
  onDeclareWorkflow,
  declareWorkflowBusy = false,
}) => {
  if (!summary) {
    return (
      <div className="jsp-panel jsp-unavailable" role="status">
        <span className="jsp-headline">Status unavailable for this page load.</span>
      </div>
    );
  }

  const headline = groupHeadline(summary);

  return (
    <div className={`jsp-panel${compact ? ' jsp-compact' : ''}`}>
      <div className="jsp-topline">
        <span className={`jsp-lifecycle ${lifecycleClass(summary)}`}>
          {lifecycleLabel(summary)}
        </span>
        {summary.readyForReview && summary.lifecycle !== 'completed' && (
          <span className="jsp-ready">Ready for review</span>
        )}
        {summary.attention.map((reason) => (
          <span key={reason} className={`jsp-attention jsp-attention-${reason}`}>
            {ATTENTION_LABELS[reason]}
          </span>
        ))}
      </div>

      {summary.workflowKnown ? (
        <div className="jsp-headline">{headline}</div>
      ) : (
        <div className="jsp-headline jsp-unknown">
          Completeness unknown — this journey has no declared workflow.
          {onDeclareWorkflow && (
            <button
              type="button"
              className="btn btn-secondary btn-sm jsp-declare-btn"
              onClick={onDeclareWorkflow}
              disabled={declareWorkflowBusy}
            >
              {declareWorkflowBusy ? 'Declaring…' : 'Declare workflow'}
            </button>
          )}
        </div>
      )}

      {summary.lifecycle === 'completed' && (
        <div className="jsp-completion-note">
          {/* G0 copy: "Marked complete by <role>" — the immutable completion
              actor snapshot from the contract. Never approval/e-sig language. */}
          Marked complete
          {summary.completedByName
            ? ` by ${summary.completedByName}${summary.completedByRole ? ` (${summary.completedByRole})` : ''}`
            : summary.completedByRole
              ? ` by ${summary.completedByRole}`
              : ''}
          {summary.completedAt ? ` on ${new Date(summary.completedAt).toLocaleDateString()}` : ''}
          {summary.completedOverride && summary.completedOverrideReason
            ? ` — with exceptions: ${summary.completedOverrideReason}`
            : ''}
        </div>
      )}

      {!compact && summary.workflowKnown && summary.components && (
        <ul className="jsp-components">
          {summary.components.map((c) => (
            <ComponentRow key={c.componentId} component={c} />
          ))}
        </ul>
      )}

      {!compact && summary.freshness.isComposite && (
        <div className="jsp-freshness-note">
          Results span more than 24 hours — this view is a composite
          {summary.freshness.oldestRequiredResultAt
            ? ` (oldest required evidence ${new Date(
                summary.freshness.oldestRequiredResultAt
              ).toLocaleDateString()})`
            : ''}
          .
        </div>
      )}
    </div>
  );
};

const ComponentRow: React.FC<{ component: ComponentProgress }> = ({ component }) => (
  <li className={`jsp-component jsp-state-${component.state}`}>
    <span className="jsp-component-label">
      {component.label}
      {!component.required && <span className="jsp-optional"> (optional)</span>}
    </span>
    <span className={`jsp-component-state jsp-state-chip-${component.state}`}>
      {STATE_LABELS[component.state]}
      {component.stale && component.state === 'complete' ? ' · stale' : ''}
    </span>
    {component.assets && component.assets.requiredAssets > 0 && (
      <span className="jsp-component-assets">
        {component.assets.completedAssets} of{' '}
        {pluralize(component.assets.requiredAssets, 'asset')} complete
      </span>
    )}
    {component.blockers.length > 0 && (
      <ul className="jsp-blockers">
        {component.blockers.map((b) => (
          <li key={b} className="jsp-blocker">
            {humanizeBlocker(b)}
          </li>
        ))}
      </ul>
    )}
  </li>
);

export default JourneyStatusPanel;
